import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useForm, FormProvider, Controller, Resolver, Path } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { formApi } from "../../api/form";
import { FileTypeEnum, FileUpload as FileUploadType, StatusEnum, FileMetadata } from "../../types";
import { createResidentFormSchema, type ResidentFormData } from "../../schemas/residentForm";
import {
  TenantFormContent,
  DependencyForm,
  BankInfoForm,
  JustificationDocsForm,
  PpeInfoForm,
} from "../../components/forms";
import { FormField } from "../../components/FormField";

// Import assets
import naefLogo from "../../assets/351cb984638c3a981c59033f28ab7dbf94a6350b.png";
import ukFlag from "../../assets/b39e4c4623a84c326b47e053ebc0303a4f7be726.svg";
import frenchFlag from "../../assets/french-flag.svg";
import { ChevronDown, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

export const ResidentForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const queryClient = useQueryClient();

  const [activeTenantIndex, setActiveTenantIndex] = useState(0);

  // Dynamic resolver that re-creates schema based on is_ppe value in form data.
  // The Zod schema type varies with isPpe, causing a type mismatch with the
  // static ResidentFormData type. We use a factory function to encapsulate
  // the type boundary cleanly.
  const dynamicResolver = useCallback(
    (...args: Parameters<Resolver<ResidentFormData>>) => {
      const [values] = args;
      const isPpeValue = values.is_ppe === true;
      const schema = createResidentFormSchema(t, isPpeValue);
      const resolve = zodResolver(schema) as Resolver<ResidentFormData>;
      return resolve(...args);
    },
    [t]
  );

  const methods = useForm<ResidentFormData>({
    resolver: dynamicResolver,
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      individual_tenants: [
        {
          status: "particulier" as const,
          civilite: undefined,
          nom: "",
          prenom: "",
          date_naissance: "",
          nationalite: "",
          email: "",
          telephone: "",
          tel_professionnel: "",
          tel_portable: "",
          rue: "",
          numero: "",
          npa: undefined,
          ville: "",
          correspondance_email: true,
          etat_civil: "celibataire" as const,
          is_guarantor: false,
          tenant_index: 0,
        },
      ],
      enterprise_tenants: [],
      cave: "",
      parking: "",
      grenier: "",
      nom_banque: "",
      titulaire_compte: "",
      iban: "",
      nom_assurance_rc: "",
      numero_police_rc: "",
      comment: "",
      // PPE fields
      numero_appartement: "",
      autre: "",
      clearing: "",
      locataire_nom: "",
      locataire_prenom: "",
      locataire_tel_prive: "",
      locataire_tel_portable: "",
      locataire_gerance: "",
      moyen_paiement: undefined,
    } as unknown as ResidentFormData,
  });

  const { handleSubmit, getValues, watch, setValue, reset } = methods;

  const [idPassportFiles, setIdPassportFiles] = useState<FileUploadType[]>([]);
  const [weddingDivorceFiles, setWeddingDivorceFiles] = useState<FileUploadType[]>([]);
  const [policeRcFiles, setPoliceRcFiles] = useState<FileUploadType[]>([]);
  const [lsvFiles, setLsvFiles] = useState<FileUploadType[]>([]);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [policeRcError, setPoliceRcError] = useState<string | undefined>(undefined);
  const [certificationChecked, setCertificationChecked] = useState(false);
  const filesRestoredRef = useRef(false);

  const currentLanguage = i18n.language as "en" | "fr";

  const handleLanguageChange = (lang: "en" | "fr") => {
    i18n.changeLanguage(lang);
  };

  // Helper: Get all tenants combined for display (sorted by tenant_index to maintain order)
  const getAllTenantsForDisplay = () => {
    const individual = (watch("individual_tenants") || []).map((t, idx) => ({
      ...t,
      tenant_index: t.tenant_index ?? idx,
      _type: "particulier" as const,
      _arrayIndex: idx,
    }));
    const enterprise = (watch("enterprise_tenants") || []).map((t, idx) => ({
      ...t,
      tenant_index: t.tenant_index ?? individual.length + idx,
      _type: "entreprise" as const,
      _arrayIndex: idx,
    }));
    // Sort by tenant_index to maintain the order in which tenants were added
    return [...individual, ...enterprise].sort(
      (a, b) => (a.tenant_index ?? 0) - (b.tenant_index ?? 0)
    );
  };

  // Helper: Get tenant ref from display index (after sorting)
  const getTenantRefFromIndex = (
    displayIndex: number
  ): { type: "particulier" | "entreprise"; arrayIndex: number } => {
    const allTenants = getAllTenantsForDisplay();
    const tenant = allTenants[displayIndex];
    if (!tenant) {
      // Fallback to old logic if tenant not found
      const individual = watch("individual_tenants") || [];
      if (displayIndex < individual.length) {
        return { type: "particulier", arrayIndex: displayIndex };
      }
      return { type: "entreprise", arrayIndex: displayIndex - individual.length };
    }
    return { type: tenant._type, arrayIndex: tenant._arrayIndex };
  };

  // Tenant management functions
  const addTenant = () => {
    const individual = getValues("individual_tenants") || [];
    const enterprise = getValues("enterprise_tenants") || [];
    // Calculate next tenant_index (max of all existing + 1)
    const allTenants = [...individual, ...enterprise];
    const maxIndex = allTenants.reduce((max, t) => {
      const idx = t.tenant_index ?? -1;
      return Math.max(max, idx);
    }, -1);
    const nextIndex = maxIndex + 1;

    const newTenant = {
      status: "particulier" as const,
      civilite: undefined as unknown as "monsieur" | "madame" | "ne_souhaite_pas_communiquer",
      nom: "",
      prenom: "",
      date_naissance: "",
      nationalite: "",
      email: "",
      telephone: "",
      tel_professionnel: "",
      tel_portable: "",
      rue: "",
      numero: "",
      npa: undefined,
      ville: "",
      correspondance_email: true,
      etat_civil: "celibataire" as const,
      is_guarantor: false, // New tenants are not guarantors by default
      tenant_index: nextIndex,
    };

    // Add the new tenant to individual_tenants
    const updatedIndividual = [...individual, newTenant];
    setValue("individual_tenants", updatedIndividual, { shouldValidate: false });

    // Manually calculate the display index by sorting all tenants (including the new one)
    // Since nextIndex is maxIndex + 1, the new tenant will always be at the end after sorting
    const individualWithIndex = updatedIndividual.map((t, idx) => ({
      ...t,
      tenant_index: t.tenant_index ?? idx,
      _type: "particulier" as const,
      _arrayIndex: idx,
    }));
    const enterpriseWithIndex = enterprise.map((t, idx) => ({
      ...t,
      tenant_index: t.tenant_index ?? individualWithIndex.length + idx,
      _type: "entreprise" as const,
      _arrayIndex: idx,
    }));
    const allTenantsSorted = [...individualWithIndex, ...enterpriseWithIndex].sort(
      (a, b) => (a.tenant_index ?? 0) - (b.tenant_index ?? 0)
    );

    // The new tenant should be at the end since it has the highest tenant_index
    // Use setTimeout to ensure React has updated the form state before setting active index
    setTimeout(() => {
      const newDisplayIndex = allTenantsSorted.length - 1;
      setActiveTenantIndex(newDisplayIndex);
    }, 0);
  };

  const removeTenant = (displayIndex: number) => {
    const allTenants = getAllTenantsForDisplay();
    if (allTenants.length <= 1) {
      // Don't allow removing the last tenant
      return;
    }

    const ref = getTenantRefFromIndex(displayIndex);
    if (ref.type === "particulier") {
      const current = getValues("individual_tenants") || [];
      const newTenants = current.filter((_, i) => i !== ref.arrayIndex);
      setValue("individual_tenants", newTenants);
    } else {
      const current = getValues("enterprise_tenants") || [];
      const newTenants = current.filter((_, i) => i !== ref.arrayIndex);
      setValue("enterprise_tenants", newTenants);
    }

    // Adjust active index if needed
    if (activeTenantIndex >= getAllTenantsForDisplay().length) {
      setActiveTenantIndex(Math.max(0, getAllTenantsForDisplay().length - 1));
    } else if (activeTenantIndex > displayIndex) {
      setActiveTenantIndex(activeTenantIndex - 1);
    }
  };

  // Load metadata
  const { data: meta, error: metaError } = useQuery({
    queryKey: ["form-meta", token],
    queryFn: () => formApi.getMeta(token!),
    enabled: !!token,
    retry: false,
  });

  // Load existing state
  const { data: state, error: stateError } = useQuery({
    queryKey: ["form-state", token],
    queryFn: () => formApi.getState(token!),
    enabled: !!token,
    retry: false,
  });

  const isPpe = meta?.is_ppe === true;

  // Propagate is_ppe flag into form data so the Zod schema can access it during validation
  useEffect(() => {
    if (meta !== undefined) {
      setValue("is_ppe", isPpe);
    }
  }, [meta, isPpe, setValue]);

  // Determine token error
  const tokenError = metaError || stateError;
  const getTokenErrorMessage = () => {
    if (!tokenError) return null;

    if (axios.isAxiosError(tokenError)) {
      const status = tokenError.response?.status;
      const detail =
        (tokenError.response?.data as { detail?: string })?.detail || tokenError.message;

      // Check for closed campaign first (more specific error)
      if (detail?.toLowerCase().includes("campaign_closed")) {
        return t("form.validation.tokenCampaignClosed");
      }
      if (status === 403 || detail?.toLowerCase().includes("expired")) {
        return t("form.validation.tokenExpired");
      }
      if (
        status === 404 ||
        detail?.toLowerCase().includes("invalid") ||
        detail?.toLowerCase().includes("not found")
      ) {
        return t("form.validation.tokenInvalid");
      }
      return detail || t("form.validation.tokenInvalid");
    }
    return (
      (tokenError instanceof Error ? tokenError.message : undefined) ||
      t("form.validation.tokenInvalid")
    );
  };

  // Load saved state into form
  useEffect(() => {
    if (
      state &&
      (state.individual_tenants.length > 0 ||
        state.enterprise_tenants.length > 0 ||
        state.cave ||
        state.parking ||
        state.grenier ||
        state.nom_banque ||
        state.titulaire_compte ||
        state.iban ||
        state.nom_assurance_rc ||
        state.numero_police_rc ||
        state.comment ||
        state.numero_appartement ||
        state.autre ||
        state.clearing ||
        state.locataire_nom ||
        state.moyen_paiement)
    ) {
      // Determine the first tenant's tenant_index (lowest tenant_index)
      const allTenantsSorted = [...state.individual_tenants, ...state.enterprise_tenants].sort(
        (a, b) => a.tenant_index - b.tenant_index
      );
      const firstTenantIndex = allTenantsSorted.length > 0 ? allTenantsSorted[0].tenant_index : 0;

      // Sort tenants by tenant_index and separate them, preserving tenant_index
      const sortedIndividual = [...state.individual_tenants]
        .sort((a, b) => a.tenant_index - b.tenant_index)
        .map((t) => ({
          status: "particulier" as const,
          civilite: t.civilite || undefined,
          nom: t.nom,
          prenom: t.prenom,
          date_naissance: t.date_naissance || "",
          nationalite: t.nationalite?.trim() || "",
          email: t.email || "",
          telephone: t.telephone || "",
          tel_professionnel: t.tel_professionnel || "",
          tel_portable: t.tel_portable || "",
          rue: t.rue || "",
          numero: t.numero || "",
          npa: t.npa ?? undefined,
          ville: t.ville || "",
          correspondance_email: t.correspondance_email,
          etat_civil: t.etat_civil || "celibataire",
          // First tenant (lowest tenant_index) should never be a guarantor
          is_guarantor: t.tenant_index === firstTenantIndex ? false : t.is_guarantor === true,
          tenant_index: t.tenant_index, // Preserve tenant_index
        }));

      const sortedEnterprise = [...state.enterprise_tenants]
        .sort((a, b) => a.tenant_index - b.tenant_index)
        .map((t) => ({
          status: "entreprise" as const,
          civilite: t.civilite || undefined,
          nom_societe: t.nom_societe,
          tel_professionnel: t.tel_professionnel || "",
          tel_portable: t.tel_portable || "",
          nom_representant: t.nom_representant,
          prenom_representant: t.prenom_representant,
          email_representant: t.email_representant || "",
          rue: t.rue || "",
          numero: t.numero || "",
          npa: t.npa ?? undefined,
          ville: t.ville || "",
          correspondance_email: t.correspondance_email ?? true,
          // First tenant (lowest tenant_index) should never be a guarantor
          is_guarantor: t.tenant_index === firstTenantIndex ? false : t.is_guarantor === true,
          tenant_index: t.tenant_index, // Preserve tenant_index
        }));

      const formData = {
        individual_tenants: sortedIndividual,
        enterprise_tenants: sortedEnterprise,
        cave: state.cave || "",
        parking: state.parking || "",
        grenier: state.grenier || "",
        nom_banque: state.nom_banque || "",
        titulaire_compte: state.titulaire_compte || "",
        iban: state.iban || "",
        nom_assurance_rc: state.nom_assurance_rc || "",
        numero_police_rc: state.numero_police_rc || "",
        comment: state.comment || "",
        // PPE-specific fields
        numero_appartement: state.numero_appartement || "",
        autre: state.autre || "",
        clearing: state.clearing || "",
        locataire_nom: state.locataire_nom || "",
        locataire_prenom: state.locataire_prenom || "",
        locataire_tel_prive: state.locataire_tel_prive || "",
        locataire_tel_portable: state.locataire_tel_portable || "",
        locataire_gerance: state.locataire_gerance || "",
        moyen_paiement: state.moyen_paiement || undefined,
        is_ppe: isPpe,
      };

      reset(formData as ResidentFormData);
      setActiveTenantIndex(0);
    }
    // Restore file metadata from saved state
    // Only restore once to avoid overwriting user's current work
    if (state?.file_keys && state.file_keys.length > 0 && !filesRestoredRef.current) {
      // Group files by type and create placeholder FileUpload objects
      const idPassportRestored: FileUploadType[] = [];
      const weddingDivorceRestored: FileUploadType[] = [];
      const policeRcRestored: FileUploadType[] = [];
      const lsvRestored: FileUploadType[] = [];

      state.file_keys.forEach((fileMetadata) => {
        // Get the original filename from metadata if available, otherwise extract from file_key
        const originalFileName =
          (fileMetadata.metadata?.original_filename as string) ||
          fileMetadata.file_key.split("/").pop() ||
          "uploaded-file";

        // Create a placeholder File object for display purposes
        // We can't restore the actual file, but we can show it was uploaded
        const placeholderFile = new File([], originalFileName, {
          type: (fileMetadata.metadata?.mime_type as string) || "application/pdf",
        });

        const fileUpload: FileUploadType = {
          file: placeholderFile,
          type: fileMetadata.file_type as FileTypeEnum,
          fileMetadata: fileMetadata,
        };

        // Group by file type
        if (fileMetadata.file_type === FileTypeEnum.ID_PASSPORT) {
          idPassportRestored.push(fileUpload);
        } else if (fileMetadata.file_type === FileTypeEnum.WEDDING_DIVORCE) {
          weddingDivorceRestored.push(fileUpload);
        } else if (fileMetadata.file_type === FileTypeEnum.POLICE_RC) {
          policeRcRestored.push(fileUpload);
        } else if (fileMetadata.file_type === FileTypeEnum.LSV) {
          lsvRestored.push(fileUpload);
        }
      });

      // Set restored files
      if (idPassportRestored.length > 0) {
        setIdPassportFiles(idPassportRestored);
      }
      if (weddingDivorceRestored.length > 0) {
        setWeddingDivorceFiles(weddingDivorceRestored);
      }
      if (policeRcRestored.length > 0) {
        setPoliceRcFiles(policeRcRestored);
      }
      if (lsvRestored.length > 0) {
        setLsvFiles(lsvRestored);
      }

      // Mark as restored to prevent re-restoring
      filesRestoredRef.current = true;
    }
  }, [state, reset, isPpe]);

  // Upload file mutation
  const uploadMutation = useMutation({
    mutationFn: async ({ file, fileType }: { file: File; fileType: FileTypeEnum }) => {
      try {
        const { upload_url, object_key, file_type } = await formApi.getUploadUrl(token!, {
          filename: file.name,
          file_type: fileType,
        });
        await formApi.uploadFile(upload_url, file);
        return { file_key: object_key, file_type };
      } catch (error: unknown) {
        // Enhance error message for better user feedback
        if (axios.isAxiosError(error)) {
          if (error.code === "ERR_NETWORK" || error.message?.includes("Network Error")) {
            throw new Error(
              "Network error: Unable to upload file. Please check your internet connection."
            );
          } else if (error.code === "ECONNABORTED") {
            throw new Error("Upload timeout: File is too large or connection is too slow.");
          } else if (error.response?.status === 403 || error.response?.status === 401) {
            throw new Error("Upload authorization failed. Please refresh the page and try again.");
          }
        }
        throw error;
      }
    },
    onError: (error: unknown) => {
      console.error("File upload mutation error:", error);
      // Don't show error message here - let uploadAllFiles handle it
      // This prevents showing multiple error messages
    },
  });

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: formApi.submit,
    onSuccess: () => {
      // Invalidate and refetch the form state query to update the read-only status
      // This will automatically show the blue "already submitted" message when isCompleted becomes true
      queryClient.invalidateQueries({ queryKey: ["form-state", token] });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onError: (error: unknown) => {
      let errorMessage = "Submission failed";

      if (axios.isAxiosError(error)) {
        if (error.code === "ERR_NETWORK" || error.message?.includes("Network Error")) {
          errorMessage =
            "Network error: Unable to save form. Please check your internet connection and try again.";
        } else if (error.response?.status === 0) {
          errorMessage = "Network error: Unable to reach the server. Please check your connection.";
        } else if (error.response?.data?.detail) {
          errorMessage = (error.response.data as { detail?: string }).detail || errorMessage;
        } else if (error.message) {
          errorMessage = error.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setMessage({
        type: "error",
        text: errorMessage,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  });

  const uploadAllFiles = async (): Promise<FileMetadata[]> => {
    const allFiles = [...idPassportFiles, ...weddingDivorceFiles, ...policeRcFiles, ...lsvFiles];
    const uploadedFiles: FileMetadata[] = [];

    // Get file keys from current frontend state (files that are still present)
    const currentFileKeys = new Set(
      allFiles.filter((f) => f.fileMetadata?.file_key).map((f) => f.fileMetadata!.file_key)
    );

    // Only include existing file metadata from saved state if they're still in the current files
    // This ensures deleted files are not included
    if (state?.file_keys) {
      for (const savedFile of state.file_keys) {
        if (currentFileKeys.has(savedFile.file_key)) {
          uploadedFiles.push(savedFile);
        }
      }
    }

    for (const fileUpload of allFiles) {
      // Check if file already has metadata
      if (fileUpload.fileMetadata) {
        // Check if not already in the list
        const exists = uploadedFiles.some((f) => f.file_key === fileUpload.fileMetadata!.file_key);
        if (!exists) {
          // Preserve existing metadata, but enhance with original filename if not present
          const enhancedMetadata: FileMetadata = {
            ...fileUpload.fileMetadata,
            metadata: {
              ...fileUpload.fileMetadata.metadata,
              // Only add original_filename if not already present and we have the file name
              original_filename:
                (fileUpload.fileMetadata.metadata?.original_filename as string) ||
                (fileUpload.file.name && fileUpload.file.name.trim() !== ""
                  ? fileUpload.file.name
                  : undefined),
              file_size:
                (fileUpload.fileMetadata.metadata?.file_size as number) ||
                (fileUpload.file.size > 0 ? fileUpload.file.size : undefined),
              mime_type:
                (fileUpload.fileMetadata.metadata?.mime_type as string) ||
                (fileUpload.file.type ? fileUpload.file.type : undefined),
            },
          };
          uploadedFiles.push(enhancedMetadata);
        }
        continue;
      }

      // Skip files that are currently uploading or have errors
      if (fileUpload.uploading || fileUpload.error) {
        continue;
      }

      try {
        // Update uploading state
        const updateFiles = (files: FileUploadType[]) =>
          files.map((f) =>
            f.file === fileUpload.file ? { ...f, uploading: true, error: undefined } : f
          );

        if (fileUpload.type === FileTypeEnum.ID_PASSPORT) {
          setIdPassportFiles(updateFiles);
        } else if (fileUpload.type === FileTypeEnum.WEDDING_DIVORCE) {
          setWeddingDivorceFiles(updateFiles);
        } else if (fileUpload.type === FileTypeEnum.POLICE_RC) {
          setPoliceRcFiles(updateFiles);
        } else if (fileUpload.type === FileTypeEnum.LSV) {
          setLsvFiles(updateFiles);
        }

        const fileMetadata = await uploadMutation.mutateAsync({
          file: fileUpload.file,
          fileType: fileUpload.type,
        });

        // Enhance fileMetadata with original filename and file size
        const enhancedMetadata: FileMetadata = {
          ...fileMetadata,
          metadata: {
            original_filename: fileUpload.file.name,
            file_size: fileUpload.file.size,
            mime_type: fileUpload.file.type,
          },
        };

        uploadedFiles.push(enhancedMetadata);

        // Update with file metadata
        const updateWithMetadata = (files: FileUploadType[]) =>
          files.map((f) =>
            f.file === fileUpload.file
              ? { ...f, uploading: false, fileMetadata: enhancedMetadata }
              : f
          );

        if (fileUpload.type === FileTypeEnum.ID_PASSPORT) {
          setIdPassportFiles(updateWithMetadata);
        } else if (fileUpload.type === FileTypeEnum.WEDDING_DIVORCE) {
          setWeddingDivorceFiles(updateWithMetadata);
        } else if (fileUpload.type === FileTypeEnum.POLICE_RC) {
          setPoliceRcFiles(updateWithMetadata);
        } else if (fileUpload.type === FileTypeEnum.LSV) {
          setLsvFiles(updateWithMetadata);
        }
      } catch (error: unknown) {
        console.error("File upload error:", error);
        let errorMessage = "Upload failed";
        if (axios.isAxiosError(error)) {
          errorMessage =
            error.message || (error.response?.data as { detail?: string })?.detail || errorMessage;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }
        const updateWithError = (files: FileUploadType[]) =>
          files.map((f) =>
            f.file === fileUpload.file ? { ...f, uploading: false, error: errorMessage } : f
          );

        if (fileUpload.type === FileTypeEnum.ID_PASSPORT) {
          setIdPassportFiles(updateWithError);
        } else if (fileUpload.type === FileTypeEnum.WEDDING_DIVORCE) {
          setWeddingDivorceFiles(updateWithError);
        } else if (fileUpload.type === FileTypeEnum.POLICE_RC) {
          setPoliceRcFiles(updateWithError);
        } else if (fileUpload.type === FileTypeEnum.LSV) {
          setLsvFiles(updateWithError);
        }

        // Don't throw error - allow form to save with other files
        // Just log the error and continue with other files
        console.warn(`Failed to upload file ${fileUpload.file.name}:`, errorMessage);
      }
    }

    return uploadedFiles;
  };

  const onSubmit = async (data: ResidentFormData, isFinal: boolean) => {
    if (!token) {
      setMessage({ type: "error", text: t("form.validation.invalidToken") });
      return;
    }

    if (isFinal && !certificationChecked) {
      setMessage({ type: "error", text: t("form.errors.certificationRequired") });
      return;
    }

    try {
      // Upload files (continue even if some fail)
      let fileMetadata: FileMetadata[] = [];
      try {
        fileMetadata = await uploadAllFiles();
      } catch (uploadError: unknown) {
        console.error("File upload error:", uploadError);
        // Show warning but continue with form submission
        const uploadErrorMessage =
          uploadError instanceof Error ? uploadError.message : "Some files failed to upload";
        setMessage({
          type: "error",
          text: `${uploadErrorMessage}. Form will be saved without failed files.`,
        });
        // Still try to save the form with whatever files were successfully uploaded
      }

      // Validate that at least one police_rc file is uploaded (only for final submission, non-PPE only)
      if (isFinal && !isPpe) {
        const hasPoliceRcFile = fileMetadata.some(
          (file) => file.file_type === FileTypeEnum.POLICE_RC
        );
        if (!hasPoliceRcFile) {
          setPoliceRcError(t("form.errors.policeRcRequired"));
          // Scroll to the documents section
          const documentsSection = document.querySelector('[data-section="documents"]');
          if (documentsSection) {
            documentsSection.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          return;
        }

        // Clear error if validation passes
        setPoliceRcError(undefined);
      } else {
        // Clear error on save (draft) or PPE mode - no police_rc validation required
        setPoliceRcError(undefined);
      }

      // Submit form with successfully uploaded files
      // Include tenant_index so backend can maintain the original order
      // Ensure correspondance_email is set for enterprise tenants
      const enterpriseTenantsWithEmail = (data.enterprise_tenants || []).map((tenant) => ({
        ...tenant,
        correspondance_email: tenant.correspondance_email ?? true,
      }));

      await submitMutation.mutateAsync({
        token,
        data: {
          individual_tenants: data.individual_tenants || [],
          enterprise_tenants: enterpriseTenantsWithEmail,
          cave: data.cave || "",
          parking: data.parking || "",
          grenier: data.grenier || "",
          nom_banque: data.nom_banque || "",
          titulaire_compte: data.titulaire_compte || "",
          iban: data.iban || "",
          nom_assurance_rc: data.nom_assurance_rc || "",
          numero_police_rc: data.numero_police_rc || "",
          comment: data.comment || "",
          // PPE-specific fields
          numero_appartement: data.numero_appartement || "",
          autre: data.autre || "",
          clearing: data.clearing || "",
          locataire_nom: data.locataire_nom || "",
          locataire_prenom: data.locataire_prenom || "",
          locataire_tel_prive: data.locataire_tel_prive || "",
          locataire_tel_portable: data.locataire_tel_portable || "",
          locataire_gerance: data.locataire_gerance || "",
          moyen_paiement: data.moyen_paiement || "",
          file_keys: fileMetadata,
          is_final: isFinal,
        },
      });
    } catch (error: unknown) {
      console.error("Save error:", error);

      // Determine error type and message
      let errorMessage = "Failed to save form";

      if (axios.isAxiosError(error)) {
        if (error.code === "ERR_NETWORK" || error.message?.includes("Network Error")) {
          errorMessage = "Network error: Please check your internet connection and try again.";
        } else if (error.response?.status === 0) {
          errorMessage = "Network error: Unable to reach the server. Please check your connection.";
        } else if (error.response?.data?.detail) {
          errorMessage = (error.response.data as { detail?: string }).detail || errorMessage;
        } else if (error.message) {
          errorMessage = error.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setMessage({
        type: "error",
        text: errorMessage,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSave = async (isFinal: boolean) => {
    if (isFinal) {
      // Ensure correspondance_email is set for all enterprise tenants before validation
      const currentEnterpriseTenants = getValues("enterprise_tenants") || [];
      currentEnterpriseTenants.forEach((tenant, index) => {
        if (tenant && tenant.correspondance_email === undefined) {
          setValue(`enterprise_tenants.${index}.correspondance_email`, true, {
            shouldValidate: false,
          });
        }
      });

      // Final submission: validate all required fields
      // Use handleSubmit with error callback to handle validation failures
      handleSubmit(
        (data: ResidentFormData) => onSubmit(data, isFinal),
        (errors) => {
          // This callback is called when validation fails
          // errors contains the validation errors
          console.error("Form validation errors:", errors);

          // Helper function to check if an error object has any field errors and get first error field
          const getFirstErrorField = (
            errorObj: Record<string, unknown> | undefined
          ): string | null => {
            if (!errorObj) return null;
            // Get the first field with an error (excluding special keys)
            const keys = Object.keys(errorObj).filter((key) => key !== "root" && key !== "message");
            return keys.length > 0 ? keys[0] : null;
          };

          // Get current form values to build tenant list (more reliable than watch in error callback)
          const formValues = getValues();
          const individual = (formValues.individual_tenants || []).map((t, idx) => ({
            ...t,
            tenant_index: t.tenant_index ?? idx,
            _type: "particulier" as const,
            _arrayIndex: idx,
          }));
          const enterprise = (formValues.enterprise_tenants || []).map((t, idx) => ({
            ...t,
            tenant_index: t.tenant_index ?? individual.length + idx,
            _type: "entreprise" as const,
            _arrayIndex: idx,
          }));
          const allTenants = [...individual, ...enterprise].sort(
            (a, b) => (a.tenant_index ?? 0) - (b.tenant_index ?? 0)
          );

          // Check each tenant in display order for errors
          for (let displayIndex = 0; displayIndex < allTenants.length; displayIndex++) {
            const tenant = allTenants[displayIndex];
            if (!tenant || tenant._arrayIndex === undefined) continue;

            let tenantError: unknown = null;
            let firstErrorField: string | null = null;

            if (tenant._type === "particulier") {
              tenantError = errors.individual_tenants?.[tenant._arrayIndex];
              firstErrorField = tenantError
                ? getFirstErrorField(tenantError as Record<string, unknown>)
                : null;
            } else if (tenant._type === "entreprise") {
              tenantError = errors.enterprise_tenants?.[tenant._arrayIndex];
              firstErrorField = tenantError
                ? getFirstErrorField(tenantError as Record<string, unknown>)
                : null;
            }

            if (firstErrorField) {
              // Switch to this tenant tab
              setActiveTenantIndex(displayIndex);

              // Focus on the first error field after tab switch
              setTimeout(() => {
                const fieldName =
                  tenant._type === "particulier"
                    ? `individual_tenants.${tenant._arrayIndex}.${firstErrorField}`
                    : `enterprise_tenants.${tenant._arrayIndex}.${firstErrorField}`;

                // Try to find the input field by name attribute
                const input = document.querySelector(
                  `input[name="${fieldName}"], textarea[name="${fieldName}"]`
                ) as HTMLInputElement | HTMLTextAreaElement | null;

                // If not found, try to find a select trigger button (for Select components)
                if (!input) {
                  // Select components might have the name in a data attribute or we can find by the field name pattern
                  const selectContainer = document.querySelector(
                    `[data-field-name="${fieldName}"]`
                  );
                  if (selectContainer) {
                    const selectButton = selectContainer.querySelector(
                      "button"
                    ) as HTMLButtonElement | null;
                    if (selectButton) {
                      selectButton.focus();
                      selectButton.scrollIntoView({ behavior: "smooth", block: "center" });
                      return;
                    }
                  }
                }

                if (input) {
                  input.focus();
                  input.scrollIntoView({ behavior: "smooth", block: "center" });
                }
              }, 150);
              return;
            }
          }

          // Check for other form-level errors (insurance, policy, etc.)
          if (errors.nom_assurance_rc) {
            setTimeout(() => {
              const input = document.querySelector(
                'input[name="nom_assurance_rc"]'
              ) as HTMLInputElement | null;
              if (input) {
                input.focus();
                input.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            }, 100);
            return;
          }

          if (errors.numero_police_rc) {
            setTimeout(() => {
              const input = document.querySelector(
                'input[name="numero_police_rc"]'
              ) as HTMLInputElement | null;
              if (input) {
                input.focus();
                input.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            }, 100);
            return;
          }
        }
      )();
    } else {
      // Save draft: bypass validation, submit whatever is filled
      // Get current form values (even if empty or invalid)
      const data = getValues();
      // Ensure we have at least an empty object to save
      const formDataToSave = Object.keys(data).length > 0 ? data : {};
      await onSubmit(formDataToSave as ResidentFormData, isFinal);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-off-white">
        <div className="bg-white rounded-2xl shadow-md p-8 text-center">
          <h2 className="text-2xl font-bold text-off-red mb-2">
            {t("form.validation.invalidToken")}
          </h2>
          <p className="text-off-grey">{t("form.validation.noToken")}</p>
        </div>
      </div>
    );
  }

  // Show token error if present
  const tokenErrorMessage = getTokenErrorMessage();
  if (tokenErrorMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-off-white">
        <div className="bg-white rounded-2xl shadow-md p-8 text-center max-w-md">
          <h2 className="text-2xl font-bold text-off-red mb-2">
            {t("form.validation.invalidToken")}
          </h2>
          <p className="text-off-grey">{tokenErrorMessage}</p>
        </div>
      </div>
    );
  }

  const isCompleted = state?.status === StatusEnum.DONE;

  return (
    <FormProvider {...methods}>
      <div className="min-h-screen bg-off-white pb-[60px] pt-[200px] md:pt-[280px]">
        {/* Header */}
        <div className="fixed top-0 left-0 right-0 z-10">
          <div className="bg-white flex items-center justify-between px-4 sm:px-8 md:px-[60px] py-3 md:py-[20px]">
            <div className="h-[24px] w-[66px] sm:h-[28px] sm:w-[80px] md:h-[33.545px] md:w-[92.467px] relative">
              <img src={naefLogo} alt="Naef" className="h-full w-full object-contain" />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="language-switcher"
                  className="flex items-center gap-2 sm:gap-[13px] cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <img
                    src={currentLanguage === "en" ? ukFlag : frenchFlag}
                    alt={currentLanguage === "en" ? "UK Flag" : "French Flag"}
                    className="w-[24px] h-[24px] sm:w-[30px] sm:h-[30px]"
                  />
                  <span className="font-bold text-off-black text-lg sm:text-xl md:text-[25.708px]">
                    {currentLanguage === "en" ? "EN" : "FR"}
                  </span>
                  <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 text-off-black" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[120px]">
                <DropdownMenuItem
                  data-testid="language-menu-en"
                  onClick={() => handleLanguageChange("en")}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <img src={ukFlag} alt="UK Flag" className="w-5 h-5" />
                  <span>English</span>
                  {currentLanguage === "en" && <Check className="w-4 h-4 ml-auto" />}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="language-menu-fr"
                  onClick={() => handleLanguageChange("fr")}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <img src={frenchFlag} alt="French Flag" className="w-5 h-5" />
                  <span>Français</span>
                  {currentLanguage === "fr" && <Check className="w-4 h-4 ml-auto" />}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="bg-[#0f054b] flex items-center justify-center h-[100px] sm:h-[120px] md:h-[145px] py-4 sm:py-6 md:py-[48px] px-4">
            <h1 className="font-bold text-xl sm:text-2xl md:text-3xl lg:text-[40px] text-white leading-[1.1] text-center">
              {isPpe ? t("form.titlePpe") : t("form.title")}
            </h1>
          </div>
        </div>

        <div className="max-w-[1240px] mx-auto px-4 sm:px-6 md:px-12 lg:px-[100px] flex flex-col gap-8 sm:gap-12 md:gap-[60px]">
          {/* Address */}
          {meta && (
            <div className="flex flex-col gap-2">
              <p className="font-bold text-off-black text-lg sm:text-xl md:text-[24px] text-center break-words">
                {meta.building_address}
              </p>
              {meta.object_name && (
                <p className="font-bold text-off-black text-base sm:text-lg md:text-[20px] text-center break-words">
                  {meta.object_name}
                </p>
              )}
            </div>
          )}

          {/* Message */}
          {/* Only show error messages, not success messages (success is indicated by the blue "already submitted" message) */}
          {message && message.type === "error" && (
            <div className="px-4 py-3 rounded-lg bg-red-100 border border-red-400 text-red-700">
              {message.text}
            </div>
          )}

          {isCompleted && (
            <div className="px-4 py-3 rounded-lg bg-blue-100 border border-blue-400 text-blue-700">
              {t("form.validation.alreadySubmitted")}
            </div>
          )}

          {/* Warning message - always displayed */}
          <p className="text-off-black text-sm sm:text-base md:text-[16px] mb-[-24px] sm:mb-[-36px] md:mb-[-48px]">
            {isPpe
              ? t("form.validation.addAllPersonsWarningPpe")
              : t("form.validation.addAllPersonsWarning")}
          </p>

          {/* Tenant Tabs & Form */}
          <div className="flex flex-col rounded-[16px] w-full overflow-hidden">
            {/* Tabs */}
            <div className="bg-off-white rounded-tl-[16px] rounded-tr-[16px] flex items-end gap-2 sm:gap-[10px] pt-2 pr-2 sm:pt-[10px] sm:pr-[10px] pb-0 pl-0 overflow-x-auto scrollbar-hide border-0 mb-0 shadow-none relative">
              <div className="flex items-end gap-2 sm:gap-[10px] min-w-max">
                {getAllTenantsForDisplay().map((tenant, displayIndex) => (
                  <button
                    key={tenant.tenant_index ?? displayIndex}
                    type="button"
                    data-testid={`tenant-tab-${displayIndex}`}
                    onClick={() => setActiveTenantIndex(displayIndex)}
                    className={`rounded-tl-[16px] rounded-tr-[16px] px-4 sm:px-6 md:px-[48px] py-2 sm:py-3 md:py-[16px] transition-colors flex-shrink-0 ${
                      activeTenantIndex === displayIndex
                        ? "bg-white text-off-red rounded-br-none relative z-0"
                        : "text-off-grey hover:text-off-red"
                    }`}
                  >
                    <p className="font-normal text-sm sm:text-base md:text-[20px] whitespace-nowrap">
                      {displayIndex === 0 ? (
                        <span>{isPpe ? t("form.tenantPpeMultiple") : t("form.tenant")}</span>
                      ) : (
                        <span>{`${isPpe ? t("form.coTenantPpe") : t("form.coTenant")} ${displayIndex}`}</span>
                      )}
                    </p>
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="add-tenant-button"
                  disabled={isCompleted}
                  className="w-[28px] h-[28px] sm:w-[32px] sm:h-[32px] flex items-center justify-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed bg-off-black rounded-[50px] flex-shrink-0 ml-1 self-center"
                  onClick={addTenant}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="sm:w-4 sm:h-4"
                  >
                    <path d="M8 3V13M3 8H13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            </div>
            {/* Form Content - Tenant section only */}
            <div className="bg-white p-4 sm:p-6 md:p-8 lg:p-[32px] flex flex-col gap-6 sm:gap-8 md:gap-[50px] rounded-bl-[16px] rounded-br-[16px] border-0">
              {/* Tenant Title */}
              <div className="flex flex-col gap-2 sm:gap-[8px] relative">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex flex-col gap-2 sm:gap-[8px] flex-1 min-w-0">
                    <p
                      data-testid="tenant-section-title"
                      className="font-bold text-off-black text-base sm:text-lg md:text-[20px] break-words"
                    >
                      {activeTenantIndex === 0
                        ? isPpe
                          ? t("form.identity.mainTenantPpeMultiple")
                          : t("form.identity.mainTenant")
                        : `${isPpe ? t("form.coTenantPpe") : t("form.coTenant")} ${activeTenantIndex}`}
                    </p>
                    <p className="font-normal text-off-black text-sm sm:text-base md:text-[16px] break-words">
                      {getAllTenantsForDisplay()[activeTenantIndex]?.status === "particulier" ? (
                        <span>{t("form.status.particulier")}</span>
                      ) : (
                        <span>{t("form.status.entreprise")}</span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
                    {/* Guarantor checkbox - top right - only show for tenants after the first one, hidden for PPE */}
                    {!isPpe &&
                      activeTenantIndex > 0 &&
                      (() => {
                        const tenant = getAllTenantsForDisplay()[activeTenantIndex];
                        if (!tenant) return null;
                        const tenantType = tenant._type;
                        const arrayIndex = tenant._arrayIndex ?? 0;
                        const guarantorFieldPath =
                          tenantType === "particulier"
                            ? `individual_tenants.${arrayIndex}.is_guarantor`
                            : `enterprise_tenants.${arrayIndex}.is_guarantor`;

                        return (
                          <Controller
                            key={`guarantor-${tenant.tenant_index ?? "unknown"}`}
                            name={guarantorFieldPath as Path<ResidentFormData>}
                            control={methods.control}
                            defaultValue={false}
                            render={({ field }) => {
                              // Explicitly ensure boolean value - handle undefined, null, or any falsy value
                              // Read directly from field.value which is reactive to form state changes
                              const isChecked = field.value === true;
                              return (
                                <div className="flex items-center gap-2 sm:gap-[10px]">
                                  <input
                                    type="checkbox"
                                    id={`guarantor-${activeTenantIndex}`}
                                    checked={isChecked}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                    onBlur={field.onBlur}
                                    disabled={isCompleted}
                                    className="w-[16px] h-[16px] border-2 rounded-[2px] flex-shrink-0 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-off-red disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{
                                      borderColor: isChecked ? "#e30615" : "#757288",
                                      backgroundColor: isChecked ? "#e30615" : "white",
                                      backgroundImage: isChecked
                                        ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='white' d='M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z'/%3E%3C/svg%3E\")"
                                        : "none",
                                      backgroundSize: "contain",
                                      backgroundRepeat: "no-repeat",
                                      backgroundPosition: "center",
                                    }}
                                  />
                                  <label
                                    htmlFor={`guarantor-${activeTenantIndex}`}
                                    className="font-bold text-off-black text-sm sm:text-base md:text-[16px] cursor-pointer whitespace-nowrap"
                                  >
                                    {t("form.identity.isGuarantor")}
                                  </label>
                                </div>
                              );
                            }}
                          />
                        );
                      })()}
                    {getAllTenantsForDisplay().length > 1 && activeTenantIndex > 0 && (
                      <button
                        type="button"
                        onClick={() => removeTenant(activeTenantIndex)}
                        disabled={isCompleted}
                        className="text-off-red text-xs sm:text-sm hover:underline disabled:opacity-50 whitespace-nowrap flex-shrink-0"
                      >
                        {isPpe ? t("form.removeTenantPpe") : t("form.removeTenant")}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Tenant Form Content - Uses sub-components */}
              {getAllTenantsForDisplay()[activeTenantIndex] && (
                <TenantFormContent
                  key={
                    getAllTenantsForDisplay()[activeTenantIndex]?.tenant_index ?? activeTenantIndex
                  }
                  displayIndex={activeTenantIndex}
                  disabled={isCompleted}
                  isPpe={isPpe}
                />
              )}
            </div>
          </div>

          {/* PPE Property Info & Tenant Contact & Payment Section */}
          {isPpe && (
            <div className="bg-white p-4 sm:p-6 md:p-8 lg:p-[32px] flex flex-col gap-4 sm:gap-6 md:gap-[25px] rounded-[16px] w-full">
              <PpeInfoForm disabled={isCompleted} />
            </div>
          )}

          {/* Dependance Section - Separate block */}
          <div className="bg-white p-4 sm:p-6 md:p-8 lg:p-[32px] flex flex-col gap-4 sm:gap-6 md:gap-[25px] rounded-[16px] w-full">
            <DependencyForm disabled={isCompleted} isPpe={isPpe} />
          </div>

          {/* Bank & Insurance Section - Non-PPE only (PPE has no bank/payment info) */}
          {!isPpe && (
            <div className="bg-white p-4 sm:p-6 md:p-8 lg:p-[32px] flex flex-col gap-4 sm:gap-6 md:gap-[25px] rounded-[16px] w-full">
              <BankInfoForm disabled={isCompleted} isPpe={isPpe} />
            </div>
          )}

          {/* Documents Section - Non-PPE only (PPE docs are inline in PpeInfoForm) */}
          {!isPpe && (
            <div
              className="bg-white p-4 sm:p-6 md:p-8 lg:p-[32px] flex flex-col gap-4 sm:gap-6 md:gap-[25px] rounded-[16px] w-full"
              data-section="documents"
            >
              <JustificationDocsForm
                token={token || undefined}
                disabled={isCompleted}
                idPassportFiles={idPassportFiles}
                setIdPassportFiles={setIdPassportFiles}
                weddingDivorceFiles={weddingDivorceFiles}
                setWeddingDivorceFiles={setWeddingDivorceFiles}
                policeRcFiles={policeRcFiles}
                setPoliceRcFiles={(files) => {
                  setPoliceRcFiles(files);
                  // Clear error when files are added
                  if (files.length > 0) {
                    setPoliceRcError(undefined);
                  }
                }}
                policeRcError={policeRcError}
              />
            </div>
          )}

          {/* Comment Section - Separate block */}
          {!isCompleted && (
            <div className="bg-white p-4 sm:p-6 md:p-8 lg:p-[32px] flex flex-col gap-4 sm:gap-6 md:gap-[25px] rounded-[16px] w-full">
              <FormField label={t("form.comment.title")}>
                <textarea
                  {...methods.register("comment")}
                  placeholder={t("form.placeholders.comment")}
                  disabled={isCompleted}
                  rows={4}
                  className="w-full px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288] resize-y min-h-[100px]"
                />
              </FormField>
            </div>
          )}

          {/* Show comment in read-only mode when form is submitted */}
          {isCompleted && state?.comment && (
            <div className="bg-white p-4 sm:p-6 md:p-8 lg:p-[32px] flex flex-col gap-4 sm:gap-6 md:gap-[25px] rounded-[16px] w-full">
              <p className="font-bold text-off-black text-base sm:text-lg md:text-[20px]">
                {t("form.comment.title")}
              </p>
              <p className="text-off-black text-sm sm:text-base md:text-[16px] whitespace-pre-wrap break-words">
                {state.comment}
              </p>
            </div>
          )}

          {/* Validation & Submit Section - Separate block */}
          {!isCompleted && (
            <div className="bg-white p-4 sm:p-6 md:p-8 lg:p-[32px] flex flex-col gap-4 sm:gap-6 md:gap-[25px] rounded-[16px] w-full">
              <p className="font-bold text-off-black text-base sm:text-lg md:text-[20px]">
                {t("form.validation.title")}
              </p>
              <div className="flex items-start gap-2 sm:gap-[10px]">
                <input
                  type="checkbox"
                  data-testid="certification-checkbox"
                  checked={certificationChecked}
                  onChange={(e) => setCertificationChecked(e.target.checked)}
                  disabled={isCompleted}
                  className="w-[16px] h-[16px] border border-[#757288] rounded-[2px] bg-white mt-0.5 flex-shrink-0"
                />
                <p className="font-bold text-off-black text-sm sm:text-base md:text-[16px] break-words">
                  {t("form.validation.certify")}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-[10px]">
                <button
                  type="button"
                  onClick={() => handleSave(true)}
                  disabled={submitMutation.isPending || !certificationChecked}
                  className="bg-[#e30615] text-white h-[48px] px-[20px] py-[8px] rounded-[8px] font-bold text-sm sm:text-base md:text-[16px] w-full sm:w-[229px] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitMutation.isPending
                    ? t("form.validation.submitting")
                    : t("form.validation.submit")}
                </button>
                <button
                  type="button"
                  onClick={() => handleSave(false)}
                  disabled={submitMutation.isPending}
                  className="border border-[#e30615] text-[#e30615] bg-white h-[48px] px-[20px] py-[8px] rounded-[8px] font-bold text-sm sm:text-base md:text-[16px] w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("form.validation.save")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </FormProvider>
  );
};
